import { useEffect, useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { ShieldCheck, TrendingUp, Users, BarChart3 } from 'lucide-react';
import Header from '../layout/Header';
import LeadersSection from '../components/LeadersSection';
import AikupCard from '../components/AikupCard';
import type { AikupItem } from '../data/aikupData';
import { useAuth } from '../contexts/AuthContext';
import { useLexLastSync } from '../contexts/LexLastSyncContext';
import SuperhumanEFVPModal from '../components/SuperhumanEFVPModal';
import GouvernanceSecuriteModal from '../components/GouvernanceSecuriteModal';

interface SecuriteItem {
  id: number;
  title: string;
  category: string;
  description: string;
  aiSuggestion: string;
  kpiScore: number;
  imageUrl?: string;
}

const comiteSecuriteData: SecuriteItem[] = [
  {
    id: 1,
    title: 'EFVP — Superhuman Mail (Microsoft 365)',
    category: 'Approbation outils & conformité (Loi 25)',
    description:
      "Évaluation des facteurs relatifs à la vie privée (EFVP) pour l’intégration de Superhuman Mail à Microsoft 365, incluant risques, mesures d’atténuation et options de décision (autoriser vs refuser).",
    aiSuggestion:
      "Document clé de gouvernance : preuve de diligence (Loi 25), analyse de risques et recommandations actionnables. Sert aussi de référence pour standardiser l’approbation de nouveaux outils (cadre répétable).",
    kpiScore: 96,
    imageUrl: 'https://images.unsplash.com/photo-1563013544-824ae1b704d3?w=800&auto=format&fit=crop&q=80',
  },
  {
    id: 2,
    title: 'Gouvernance — Comité Sécurité',
    category: 'Gouvernance & cadre (à compléter)',
    description:
      "Cadre de gouvernance du comité Sécurité : structure, rituels, RACI, maturité et roadmap. Modal clonée (format) pour bâtir le contenu avec François & Vincent.",
    aiSuggestion:
      "Carte structurante : sert de “charte” du comité. Permet de rendre les décisions répétables, de standardiser l’analyse sécurité/IA et de nourrir le chatbot avec une gouvernance claire.",
    kpiScore: 93,
    imageUrl: 'https://images.unsplash.com/photo-1550751827-4bd374c3f58b?w=800&auto=format&fit=crop&q=80',
  },
];

export default function ComiteSecurite() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { dates: lexDates } = useLexLastSync();
  const [isSuperhumanOpen, setIsSuperhumanOpen] = useState(false);
  const [isGouvernanceOpen, setIsGouvernanceOpen] = useState(false);

  // Accès restreint (spécifique) au Comité Sécurité
  const emailsAutorisesSecurite = useMemo(
    () => [
      'dany.bourassa@bzinc.ca',
      'francois.delisle@bzinc.ca',
      'vincent.bourassa@bzinc.ca',
      'stephane.paradis@bzinc.ca',
      'eric.hains@bzinc.ca',
      'eric.durand@bzinc.ca',
    ],
    []
  );

  const estAutorise = user?.email && emailsAutorisesSecurite.includes(user.email.toLowerCase());

  useEffect(() => {
    if (!user?.email) {
      navigate('/comites');
      return;
    }

    if (!estAutorise) {
      navigate('/comites');
    }
  }, [user, navigate, estAutorise]);

  if (!user?.email) {
    return null;
  }
  if (!estAutorise) {
    return null;
  }

  const leaders = [
    {
      name: 'François Delisle',
      title: 'VP Technologie & Sécurité • Owner comité',
      avatar: '/FP.png',
      vision:
        "Le Comité Sécurité existe pour protéger BZ : posture cyber, gouvernance des exceptions, conformité (Loi 25) et contrôle des risques liés aux outils et aux accès.",
      attentes:
        "Décider vite, documenter chaque décision, garder une trace auditable et garder l’organisation dans une posture acceptable — sans freiner inutilement la productivité.",
      pagePRIsME:
        "Cette page est le coffre-fort décisionnel : EFVP, preuves, questions fournisseurs, risques, mesures d’atténuation et décisions signées.",
    },
    {
      name: 'Vincent Bourassa',
      title: 'Conseiller en cybersécurité • Évaluations & exécution',
      avatar: '/VB.png',
      vision:
        "Vincent structure l’évaluation et l’exécution : permissions, scoping, contrôles, journaux, et tout ce qui rend la décision reproductible et défendable.",
      attentes:
        "Avoir un cadre simple et répété : mêmes sections, mêmes questions, mêmes preuves, pour éviter les décisions “à l’instinct”.",
      pagePRIsME:
        "La page sert de standard : on documente les demandes d’outils, on évalue et on peut répondre aux audits sans stress.",
    },
  ];

  const efvpCardLeaders = [
    { name: 'Vincent Bourassa', avatar: '/VB.png', title: 'Vincent Bourassa — Évaluation initiale' },
    { name: 'François Delisle', avatar: '/FP.png', title: 'François Delisle — Évaluation initiale' },
    { name: 'Dany Bourassa', avatar: '/DB.png', title: 'Dany Bourassa — Demandeur' },
  ];

  const gouvernanceCardLeaders = [
    { name: 'Vincent Bourassa', avatar: '/VB.png', title: 'Vincent Bourassa' },
    { name: 'François Delisle', avatar: '/FP.png', title: 'François Delisle' },
  ];

  const totalItems = comiteSecuriteData.length;
  const avgKpiScore = Math.round(comiteSecuriteData.reduce((sum, item) => sum + item.kpiScore, 0) / totalItems);
  const categories = Array.from(new Set(comiteSecuriteData.map((item) => item.category)));
  const highPriorityItems = comiteSecuriteData.filter((item) => item.kpiScore >= 90).length;

  const handleCardClick = (item: SecuriteItem) => {
    if (item.id === 1) setIsSuperhumanOpen(true);
    if (item.id === 2) setIsGouvernanceOpen(true);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
      <Header
        title="Comité Sécurité"
        subtitle="Posture cyber, conformité (Loi 25), gestion des risques et approbation des outils. Décisions documentées, traçables et défendables."
      />

      <div className="max-w-7xl mx-auto px-8 pb-12">
        <LeadersSection leaders={leaders} title="Membres & contributeurs (EFVP / approbation outils)" />

        {/* Stats Overview */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white rounded-xl shadow-md p-6 border-l-4 border-emerald-500"
          >
            <div className="flex items-center gap-3 mb-2">
              <BarChart3 className="w-5 h-5 text-emerald-600" />
              <p className="text-sm text-gray-600">Outils disponibles</p>
            </div>
            <p className="text-3xl font-bold text-gray-900">{totalItems}</p>
            <p className="text-xs text-gray-500 mt-1">Dossiers & décisions</p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="bg-white rounded-xl shadow-md p-6 border-l-4 border-green-500"
          >
            <div className="flex items-center gap-3 mb-2">
              <TrendingUp className="w-5 h-5 text-green-600" />
              <p className="text-sm text-gray-600">Score excellence</p>
            </div>
            <p className="text-3xl font-bold text-gray-900">{avgKpiScore}%</p>
            <p className="text-xs text-gray-500 mt-1">Moyenne KPI</p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="bg-white rounded-xl shadow-md p-6 border-l-4 border-teal-500"
          >
            <div className="flex items-center gap-3 mb-2">
              <ShieldCheck className="w-5 h-5 text-teal-600" />
              <p className="text-sm text-gray-600">Critiques</p>
            </div>
            <p className="text-3xl font-bold text-gray-900">{highPriorityItems}</p>
            <p className="text-xs text-gray-500 mt-1">Dossiers 90%+</p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="bg-white rounded-xl shadow-md p-6 border-l-4 border-cyan-500"
          >
            <div className="flex items-center gap-3 mb-2">
              <Users className="w-5 h-5 text-cyan-600" />
              <p className="text-sm text-gray-600">Domaines</p>
            </div>
            <p className="text-3xl font-bold text-gray-900">{categories.length}</p>
            <p className="text-xs text-gray-500 mt-1">Catégories couvertes</p>
          </motion.div>
        </div>

        {/* Cards par catégorie */}
        {categories.map((category, categoryIndex) => {
          const itemsInCategory = comiteSecuriteData.filter((item) => item.category === category);

          return (
            <div key={category} className="mb-12">
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: categoryIndex * 0.1 }}
                className="flex items-center gap-3 mb-6"
              >
                <div className="flex-1 h-px bg-gradient-to-r from-emerald-500 to-teal-500"></div>
                <h2 className="text-2xl font-bold text-gray-800 px-4">{category}</h2>
                <div className="flex-1 h-px bg-gradient-to-l from-emerald-500 to-teal-500"></div>
              </motion.div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {itemsInCategory.map((item, idx) => {
                  const aikupItem: AikupItem = {
                    id: item.id,
                    title: item.title,
                    category: item.category,
                    description: item.description,
                    aiSuggestion: item.aiSuggestion,
                    kpiScore: item.kpiScore,
                    imageUrl: item.imageUrl,
                    ...(item.id === 1 && {
                      inKnowledgeBase: true,
                      lastSyncDate: lexDates['efvp_superhuman_mail_last_ai_analysis'] ?? undefined,
                    }),
                    ...(item.id === 2 && {
                      inKnowledgeBase: true,
                      lastSyncDate: lexDates['gouvernance_comite_securite_last_ai_analysis'] ?? undefined,
                    }),
                  };

                  return (
                    <motion.div
                      key={item.id}
                      initial={{ opacity: 0, scale: 0.9 }}
                      animate={{ opacity: 1, scale: 1 }}
                      transition={{ delay: categoryIndex * 0.1 + idx * 0.05 }}
                    >
                      <AikupCard
                        item={aikupItem}
                        onClick={() => handleCardClick(item)}
                        onExpertClick={() => {}}
                        index={idx}
                        customLeaders={item.id === 1 ? efvpCardLeaders : item.id === 2 ? gouvernanceCardLeaders : undefined}
                        stampText={item.id === 1 ? 'APPROVED' : undefined}
                      />
                    </motion.div>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>

      <SuperhumanEFVPModal isOpen={isSuperhumanOpen} onClose={() => setIsSuperhumanOpen(false)} />
      <GouvernanceSecuriteModal isOpen={isGouvernanceOpen} onClose={() => setIsGouvernanceOpen(false)} />
    </div>
  );
}

